import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import MergeContractIcons from '@/icons/MergeContractIcons'
import { getContractsByCustomerId } from '@/modules/Customer/services'
import type { ContractAndService } from '@/modules/Customer/types'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useModal } from '@/contexts/modal'
import {
    getContractColumns,
    getContractFilterItems,
} from '../utils/customer-contracts-config'

/**
 * Filters interface for Customer Contracts
 */
interface ContractFilters {
    contract_status: string[]
    contract_role: string[]
    contract_direction: string[]
    sla_enable: string[]
    nda_enable: string[]
    auto_renewable: string[]
}

/**
 * Business logic hook for Customer Contracts
 * Manages data fetching, pagination, sorting, and merge functionality
 */
export const useCustomerContracts = () => {
    const { id } = useParams()
    const { t } = useTranslation(['contract'])
    const { setModal } = useModal()

    // URL params for pagination, sorting and filters
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<ContractFilters>({
        defaults: { sort_by: 'contract_id', sort_order: 'desc', page_size: 10 },
        filterDefaults: {
            contract_status: [],
            contract_role: [],
            contract_direction: [],
            sla_enable: [],
            nda_enable: [],
            auto_renewable: [],
        },
    })

    // Form for filter controls
    const { control } = useForm<ContractFilters>({
        defaultValues: filters,
    })

    // Fetch contracts data
    const { data, renderState, isFetching } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CUSTOMERS, QUERY_KEYS.CONTRACTS, id, queryParams],
        queryFn: () =>
            getContractsByCustomerId({
                ...queryParams,
                customer_id: Number(id),
            }),
        placeholderData: true,
    })

    // Selection state for merge functionality
    const [selectedContracts, setSelectedContracts] = useState<string[]>([])

    // Handle merge contracts with modal
    const handleMergeContracts = useCallback(
        (selectedRows: ContractAndService[]) => {
            const contractIds = selectedRows.map((row) => row.contract_id)

            setModal('ContractModals/MergedContractModal/index', {
                contractIDs: contractIds,
                customerID: Number(id),
                handleClose: () => {
                    setSelectedContracts([])
                },
            })
        },
        [setModal, id]
    )

    // Memoized columns configuration
    const columns = useMemo(() => getContractColumns(t), [t])

    // Memoized filter configuration
    const filterItems = useMemo(() => getContractFilterItems(t), [t])

    // Selection actions
    const selectionActions = useMemo(
        () => [
            {
                key: 'merge',
                label: t('merge-contract'),
                icon: <MergeContractIcons />,
                onClick: handleMergeContracts,
                disabled: (rows: ContractAndService[]) => rows.length < 2,
                variant: 'default' as const,
            },
        ],
        [t, handleMergeContracts]
    )

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )

    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    const handleSelectionChange = useCallback((keys: string[]) => {
        setSelectedContracts(keys)
    }, [])

    return {
        // Data
        contracts: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),
        isFetching,

        // Params & Filters
        params,
        searchInputProps,

        // Configs
        filters,
        control,
        setFilter,
        columns,
        selectionActions,
        filterItems,

        // Selection
        selectedContracts,
        handleSelectionChange,

        // Handlers
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,

        // Translation
        t,
    }
}
